import React, { useState, useEffect } from 'react';
import { ACTIVITY_TYPES } from '../constants/activityTypes';
import PhoneInput from './PhoneInput';
import './AddClubModal.css';

interface Club {
  id: number;
  name: string;
  website_url?: string;
  location?: string;
  venue?: string;
  activity_type: string;
  cost?: number;
  max_participants?: number;
  contact_phone?: string;
  contact_email?: string;
  is_private?: boolean;
}

interface AddClubModalProps {
  onSave: (clubData: {
    name: string;
    website_url: string;
    location: string;
    venue: string;
    activity_type: string;
    cost: string;
    max_participants: string;
    contact_phone: string;
    contact_email: string;
    is_private: boolean;
  }) => void;
  onCancel: () => void;
  editClub?: Club;
}

const AddClubModal: React.FC<AddClubModalProps> = ({ onSave, onCancel, editClub }) => {
  const [clubName, setClubName] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [location, setLocation] = useState('');
  const [venue, setVenue] = useState('');
  const [activityType, setActivityType] = useState('');
  const [cost, setCost] = useState('');
  const [maxParticipants, setMaxParticipants] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [isPrivate, setIsPrivate] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Populate form if editing an existing club
  useEffect(() => {
    if (editClub) {
      setClubName(editClub.name || '');
      setWebsiteUrl(editClub.website_url || '');
      setLocation(editClub.location || '');
      setVenue(editClub.venue || '');
      setActivityType(editClub.activity_type || '');
      setCost(editClub.cost?.toString() || '');
      setMaxParticipants(editClub.max_participants?.toString() || '');
      setContactPhone(editClub.contact_phone || '');
      setContactEmail(editClub.contact_email || '');
      setIsPrivate(editClub.is_private || false);
    }
  }, [editClub]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!clubName.trim()) {
      newErrors.name = 'Club name is required';
    }

    // Website URL is optional, but if provided must be valid
    if (websiteUrl.trim() && !websiteUrl.match(/^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/)) {
      newErrors.website_url = 'Please enter a valid URL';
    }

    if (!location.trim()) {
      newErrors.location = 'Town/City is required';
    }

    if (!activityType) {
      newErrors.activity_type = 'Activity type is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      onSave({
        name: clubName,
        website_url: websiteUrl,
        location: location,
        venue: venue,
        activity_type: activityType,
        cost: cost,
        max_participants: maxParticipants,
        contact_phone: contactPhone,
        contact_email: contactEmail,
        is_private: isPrivate
      });
    }
  };

  return (
    <div className="add-club-modal-overlay" onClick={onCancel}>
      <div className="add-club-modal" onClick={(e) => e.stopPropagation()}>
        <div className="add-club-modal-header">
          <h2>{editClub ? 'Edit Club' : 'Add New Club'}</h2>
          <button
            onClick={onCancel}
            className="close-btn"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <div className="add-club-modal-content">
          <div className="form-group">
            <label htmlFor="club_name">Club Name *</label>
            <input
              id="club_name"
              type="text"
              value={clubName}
              onChange={(e) => setClubName(e.target.value)}
              placeholder="e.g. Tigers Swimming Club"
              className={`form-input ${errors.name ? 'error' : ''}`}
            />
            {errors.name && <span className="error-message">{errors.name}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="website_url">Website URL (Optional)</label>
            <input
              id="website_url"
              type="url"
              value={websiteUrl}
              onChange={(e) => setWebsiteUrl(e.target.value)}
              placeholder="https://example.com"
              className={`form-input ${errors.website_url ? 'error' : ''}`}
            />
            {errors.website_url && <span className="error-message">{errors.website_url}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="location">Town/City *</label>
            <input
              id="location"
              type="text"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="e.g., London, Manchester"
              className={`form-input ${errors.location ? 'error' : ''}`}
            />
            {errors.location && <span className="error-message">{errors.location}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="venue">Venue (Optional)</label>
            <input
              id="venue"
              type="text"
              value={venue}
              onChange={(e) => setVenue(e.target.value)}
              placeholder="e.g., Community Center, School Hall"
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="activity_type">Activity Type *</label>
            <select
              id="activity_type"
              value={activityType}
              onChange={(e) => setActivityType(e.target.value)}
              className={`form-input ${errors.activity_type ? 'error' : ''}`}
            >
              <option value="">Select activity type</option>
              {ACTIVITY_TYPES.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
            {errors.activity_type && <span className="error-message">{errors.activity_type}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="cost">Cost (£) (Optional)</label>
            <input
              id="cost"
              type="number"
              step="0.01"
              min="0"
              value={cost}
              onChange={(e) => setCost(e.target.value)}
              placeholder="0.00"
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="max_participants">Maximum Participants (Optional)</label>
            <input
              id="max_participants"
              type="number"
              min="1"
              value={maxParticipants}
              onChange={(e) => setMaxParticipants(e.target.value)}
              placeholder="e.g., 20"
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="contact_phone">Club Phone Number (Optional)</label>
            <PhoneInput
              value={contactPhone}
              onChange={(value) => setContactPhone(value)}
              className="form-input"
              placeholder="Contact phone"
            />
          </div>

          <div className="form-group">
            <label htmlFor="contact_email">Club Email (Optional)</label>
            <input
              id="contact_email"
              type="email"
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
              placeholder="contact@example.com"
              className="form-input"
            />
          </div>

          <div className="form-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={isPrivate}
                onChange={(e) => setIsPrivate(e.target.checked)}
                className="checkbox-input"
              />
              <span className="checkbox-text">
                🔒 Keep this club private (only visible to you)
              </span>
            </label>
            <p className="help-text">
              Private clubs won't appear in the browse clubs list for other users
            </p>
          </div>
        </div>

        <div className="add-club-modal-footer">
          <button
            onClick={onCancel}
            className="cancel-btn"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="save-btn"
          >
            {editClub ? 'Update Club' : 'Save Club'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddClubModal;
